import { StyleSheet } from 'react-native';

import type { useTheme } from './theme';
import { webTapFloor } from '../theme';

// Styles for <EntryTipModal>, split out when the LG-61 band work pushed the
// component past the 500-line cap. Same sibling idiom as
// `HistoryPanel.styles.ts`: the component keeps the logic, the sheet keeps
// the tokens.

export function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    // The tap target lives on the Pressable, not the glyph: react-native-web's
    // Pressable ignores hitSlop (DS-48), so the ✕ that every athlete taps to
    // close needs a REAL box — webTapFloor (44 on web, undefined on native) is
    // the web-only floor; on native hitSlop=8 tops the 28pt glyph to ≥44.
    // minWidth 32 keeps native width as it was before (32 + hitSlop = 48).
    closeBtn: {
      minWidth: webTapFloor ?? 32,
      minHeight: webTapFloor,
      alignItems: 'center',
      justifyContent: 'center',
    },
    close: {
      fontSize: 28,
      lineHeight: 28,
      color: p.ink2,
    },
    title: {
      flex: 1,
      textAlign: 'center',
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: '600',
      paddingHorizontal: s.s3,
    },
    // Balances the close box so the centred title stays centred (matches its
    // per-platform footprint: 44 on web, 32 on native).
    headerSpacer: { width: webTapFloor ?? 32 },
    body: {
      padding: s.s5,
      gap: s.s5,
    },
    painBanner: {
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      gap: s.s1,
    },
    painBannerTitle: {
      color: '#ffffff',
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    painBannerBody: {
      color: '#ffffff',
      // Reduced-vision rule: this line carries safety info (which painful
      // muscle this exercise loads, and which side), so it reads at body
      // size, not the smaller `sm` a caption would use.
      fontSize: ty.sizes.base,
      fontWeight: '500',
      textTransform: 'capitalize',
    },
    // The "(left side only)" / "(right side only)" span nested inside
    // painBannerBody. `capitalize` on the parent would otherwise title-case
    // this too (DE: "Nur Rechte Seite"), which reads as an error, not
    // emphasis: `none` here overrides the inherited transform for just
    // this span, leaving the muscle name itself capitalized.
    painBannerSide: {
      textTransform: 'none',
    },
    description: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm * ty.leading.base,
    },
    // Coach / program note blocks — brand left-rail to read as authored
    // guidance, distinct from the catalog description below.
    noteBlock: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      borderLeftWidth: 3,
      borderLeftColor: p.brand500,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      gap: s.s1,
    },
    noteBlockLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '800',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    noteBlockText: {
      color: p.ink,
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * ty.leading.base,
    },
    // Per-implement logging tip — a subtle info card (not the brand note rail,
    // which is reserved for coach/program guidance). Readable body size.
    loadingTip: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    loadingTipText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm * ty.leading.base,
    },
    bodySection: { gap: s.s3 },
    legendRow: {
      flexDirection: 'row',
      gap: s.s5,
      justifyContent: 'flex-start',
    },
    bodyMapWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
    },
    // The history page's own scroll area, inside the fixed band.
    historyPage: {
      paddingBottom: s.s3,
    },
    altsSection: { gap: s.s2 },
    altsLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    altsEmpty: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontStyle: 'italic',
    },
    altRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: s.s3,
      paddingHorizontal: s.s4,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      minHeight: 44,
    },
    altRowPressed: {
      backgroundColor: p.surface2,
    },
    altLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      flex: 1,
    },
    altName: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '500',
      flex: 1,
    },
    altChevron: {
      color: p.ink3,
      fontSize: ty.sizes.md,
      marginLeft: s.s3,
    },
  });
}
